import React, {useEffect, useMemo, useRef, useState} from "react";
import paginationFactory, {PaginationProvider} from "react-bootstrap-table2-paginator";
import {Col, Row} from "reactstrap";
import {CardHeader} from "@material-ui/core";
import {
    getAllCompanyUsers,
    getCameraEnabledLocations,
    getCameraLabelMappingByCompanyId,
    getCompaniesList,
    getTotalCamerasByLocationId,
    addCameraLabelMapping,
    updateCameraLabelMapping
} from "../_redux";
import SVG from "react-inlinesvg";
import {
    entityFilter, getFilteredAndPaginatedEntities, getPaginationOptions, toAbsoluteUrl
} from "../../../../_metronic/_helpers";
import {successToast, warningToast} from "../../../../utils/ToastMessage";
import {Card, CardBody, Pagination} from "../../../../_metronic/_partials/controls";
import {SearchText} from "../../../../utils/SearchText";
import CustomizedButtons from "../../../../utils/SuperAdmin/CustomizedButtons";
import TableLoader from "../../../../utils/SuperAdmin/Loader/TableLoader";
import {AutoServingTable} from "../../../../utils/AutoServingTable";
import {useResultManagerLabelUIContext} from "./ResultManagerLabelUIContext";
import ReactSelectDropDownCommon from "../../../../utils/SuperAdmin/ReactSelectDropDownCommon";
import {fetchCompanies} from "../../../../utils/commonFunction";
import {Modal} from "react-bootstrap";

export default function AddResultManagerLabel() {
    const resultManagerLabelUIContext = useResultManagerLabelUIContext();
    const resultManagerLabelUIProps = useMemo(() => resultManagerLabelUIContext, [resultManagerLabelUIContext]);

    const [data, setData] = useState([]);
    const [blocking, setBlocking] = useState(false);

    const [selectedUserId, setSelectedUserId] = useState("");

    const [companyOptions, setCompanyOptions] = useState([]);

    const searchInput = useRef("");
    const [filterEntities, setFilterEntities] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);

    const [selectedLabels, setSelectedLabels] = useState([]);

    const [addLabelModalOpen, setAddLabelModalOpen] = useState(false);
    const [labelModalOpen, setLabelModalOpen] = useState(false);

    const [companies, setCompanies] = useState([]);
    const [locations, setLocations] = useState([]);
    const [cameras, setCameras] = useState([]);

    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [selectedLocationId, setSelectedLocationId] = useState(null);
    const [selectedCameraId, setSelectedCameraId] = useState(null);
    const [labelText, setLabelText] = useState("");

    const columns = [{dataField: "id", text: "INDEX", sort: true}, {
        dataField: "company", text: "Company Name", sort: true
    }, {dataField: "location", text: "Location Name", sort: true}, {
        dataField: "camera", text: "Camera Name", sort: true
    }, {
        dataField: "labels", text: "Labels", formatter: (cellContent, row) => {
            if (!cellContent) return "-";

            const labelsArray = cellContent.split(",");
            const preview = labelsArray.slice(0, 3); // only show 2–3 labels
            const hiddenCount = labelsArray.length - preview.length;

            return (<div>
                {preview.map((lbl, idx) => (<span
                    key={idx}
                    className="badge badge-primary mr-1"
                >
            {lbl}
          </span>))}
                {hiddenCount > 0 && (<span
                    className="badge badge-info cursor-pointer"
                    onClick={() => handleOpenLabelModal(labelsArray)}
                >
            +{hiddenCount} more
          </span>)}
            </div>);
        }
    }, {
        dataField: "assignlocation", text: "Actions", formatter: (cellContent, row) => (<>
            <div
                className="btn btn-icon mr-4 btn-light btn-hover-primary btn-hover-light-inverse btn-sm mx-3"
                onClick={() => handleAssignCompany(cellContent, row)}
            >
          <span className="svg-icon svg-icon-md svg-icon-primary">
            <SVG title="Assign locations" src={toAbsoluteUrl("/media/svg/icons/Communication/Write.svg")}/>
          </span>
            </div>
        </>),
    },];

    // Fetch supervisors
    const fetchResultManagerList = (selectedCompany) => {
        setBlocking(true);
        const company_id = !selectedCompany || selectedCompany?.value === "-1" ? "-1" : selectedCompany?.value;
        getCameraLabelMappingByCompanyId(company_id)
            .then((response) => {
                if (response?.isSuccess) {
                    const responseData = response.data;
                    const formattedData = responseData.map((obj, index) => {
                        const companyName = obj.Company?.company_name || "-";
                        const locationName = obj.Location?.location_name || "-";
                        const cameraName = obj.DeploymentJobRTSPManager?.camera_name || "-";
                        const labels = obj.CameraLabelMapping?.labels || "-";

                        return {
                            id: index + 1,
                            company: companyName,
                            location: locationName,
                            camera: cameraName,
                            labels: labels, // IDs for edit prefill
                            companyId: obj.Company?.id,
                            locationId: obj.Location?.id,
                            cameraId: obj.DeploymentJobRTSPManager?.id,
                            assignlocation: [{
                                user_status: obj.DeploymentJobRTSPManager?.status ?? true,
                                location: [{label: locationName, value: obj.Location?.id}],
                                id: obj.CameraLabelMapping?.id,
                            },],
                        };
                    });


                    setData(formattedData);
                    setFilterEntities(formattedData);
                }
                setBlocking(false);
            })
            .catch((error) => {
                setBlocking(false);
                warningToast(error?.detail || "Something went wrong!");
            });
    };

    const getAllCompaniesList = () => {
        getCompaniesList()
            .then((response) => {
                if (response?.isSuccess) {
                    const responseData = response.data;
                    const companyValues = responseData?.map((obj) => ({
                        label: obj.company_name, value: obj.id,
                    })) || [];
                    const withAllOption = [{label: "All Company", value: "-1"}, ...companyValues,];
                    setCompanyOptions(withAllOption);
                    setSelectedCompany({label: "All Company", value: "-1"});
                }
                setBlocking(false);
            })
            .catch((error) => {
                setBlocking(false);
                warningToast(error?.detail || "Something went wrong!");
            });
    };

    const handleOpenAddLabelModal = () => {
        setAddLabelModalOpen(true);
        // load companies when modal opens
        getAllCompanyUsers()
            .then(res => {
                if (res?.isSuccess) {
                    const companyList = res.data.map(c => ({
                        label: c?.company?.company_name, value: c?.company?.id,
                        user_id: c?.id,
                    }));
                    setCompanies(companyList);
                }
            })
            .catch(err => warningToast(err?.detail || "Error fetching companies"));
    };

    const handleCompanyChange = (company) => {
        setSelectedCompanyId(company?.value);
        setSelectedLocationId(null);
        setSelectedCameraId(null);
        setLocations([]);
        setCameras([]);

        if (company?.value) {
            getCameraEnabledLocations(company.value)
                .then(res => {
                    if (res?.isSuccess) {
                        const locationList = res.data.map(l => ({
                            label: l.location_name, value: l.id,
                        }));
                        setLocations(locationList);
                    }
                })
                .catch(err => warningToast(err?.detail || "Error fetching locations"));
        }
    };


    // Triggers for re-fetch
    useEffect(() => {
        fetchResultManagerList(selectedCompany);
        getAllCompaniesList()
        const loadCompanies = async () => {
            await fetchCompanies(setCompanyOptions, setSelectedCompany);
        };
        loadCompanies();
        //eslint-disable-next-line
    }, []);


    const handleAssignCompany = (cellContent, row) => {
        setSelectedUserId(row);
        // Load companies for dropdown
        let companyListData = [];
        getAllCompanyUsers()
            .then(res => {
                if (res?.isSuccess) {
                    const companyList = res.data.map(c => ({
                        label: c.company?.company_name, value: c.company?.id,
                        user_id: c.id,
                    }));
                    companyListData = companyList
                    console.log("companyList::::::::", companyList);
                    setCompanies(companyList);
                }
            })
            .catch(err => warningToast(err?.detail || "Error fetching companies"));
        // Prefill values for edit
        setLabelText(row?.labels || "");
        const companyId = row?.companyId;
        const locationId = row?.locationId;
        const camId = row?.cameraId;
        if (companyId) {
            setSelectedCompanyId(companyId);
            // Load locations then set selected
            console.log("row::::::::", row);
            getCameraEnabledLocations(companyId)
                .then(res => {
                    if (res?.isSuccess) {
                        const locationList = res.data.map(l => ({
                            label: l.location_name, value: l.id,
                        }));
                        setLocations(locationList);
                        if (locationId) {
                            setSelectedLocationId(locationId);
                            // Load cameras then set selected
                            console.log("row::::::::244", row);
                            console.log("companies:::::::: 244", companyListData);
                            console.log("companies:::::::: 244", companies);
                            const selectedCompanyObj = companyListData.find(
                                (c) => c.value === row?.companyId
                            );
                            console.log("253::::::::" ,companyListData)
                            console.log("253::::::::" ,row?.companyId)
                            console.log("253::::::::" , selectedCompanyObj)
                            const userId = selectedCompanyObj?.user_id;
                            getTotalCamerasByLocationId(userId ,[locationId])
                                .then(cRes => {
                                    if (cRes?.isSuccess) {
                                        const cameraList = cRes.data.map(c => ({
                                            label: c.camera_name, value: c.id,
                                        }));
                                        setCameras(cameraList);
                                        if (camId) {
                                            setSelectedCameraId(camId);
                                        }
                                    }
                                })
                                .catch(() => {
                                });
                        }
                    }
                })
                .catch(() => {
                });
        }
        setAddLabelModalOpen(true)
    };

    const filterResultManager = (e) => {
        const searchStr = e?.target?.value || searchInput.current.value;
        const keys = ["camera", "company", "location"]; // 👈 search by camera name now
        entityFilter(data, searchStr, keys, resultManagerLabelUIProps.queryParams, setFilterEntities);
    };


    useEffect(() => {
        filterResultManager();
        //eslint-disable-next-line
    }, [data]);

    const currentItems = getFilteredAndPaginatedEntities(filterEntities, resultManagerLabelUIProps.queryParams);


    const handleChangeCompany = (option) => {
        setSelectedCompany(option);
        fetchResultManagerList(option);
    }

    const handleOpenLabelModal = (labels) => {
        setSelectedLabels(labels);
        setLabelModalOpen(true);
    };

    const handleCloseLabelModal = () => {
        setLabelModalOpen(false);
    };

    const handleSubmitLabel = () => {
        // Decide add or update based on selectedUserId
        const isEdit = Boolean(selectedUserId && selectedUserId.id);

        // Validate inputs for add/update
        if (!isEdit) {
            if (!selectedCompanyId || !selectedLocationId || !selectedCameraId || !labelText) {
                warningToast("All fields are required");
                return;
            }
        } else {
            // For edit, allow updating labelText only if provided; still require mapping id
            if (!selectedUserId?.assignlocation?.[0]?.id) {
                warningToast("Invalid selection to update");
                return;
            }
        }

        // Build payloads
        const addPayload = {
            // company_id: selectedCompanyId,
            // location_id: selectedLocationId,
            camera_id: selectedCameraId,
            labels: labelText,
        };

        const updatePayload = {
            id: selectedUserId?.assignlocation?.[0]?.id, labels: labelText || selectedUserId?.labels,
            camera_id: selectedCameraId,
        };

        const apiCall = isEdit ? updateCameraLabelMapping(updatePayload) : addCameraLabelMapping(addPayload);

        apiCall
            .then((res) => {
                if (res?.isSuccess) {
                    successToast(isEdit ? "Label updated successfully" : "Label added successfully");
                    setAddLabelModalOpen(false);
                    // Refresh table
                    fetchResultManagerList(selectedCompany);
                } else {
                    warningToast(res?.detail || "Operation failed");
                }
            })
            .catch((err) => warningToast(err?.detail || "Operation failed"));
    };

    const handleLocationChange = (location) => {
        setSelectedLocationId(location?.value);
        setSelectedCameraId(null);
        setCameras([]);
        console.log("location::::::::", companies);
        if (location?.value) {
            console.log("location::::::::348",location.value, companies , companies?.user_id ,selectedCompanyId) ;
            const selectedCompanyObj = companies.find(
                (c) => c.value === selectedCompanyId
            );

            const locationId =[location?.value];
            const userId = selectedCompanyObj?.user_id;
            getTotalCamerasByLocationId(userId ,locationId)
                .then(res => {
                    if (res?.isSuccess) {
                        const cameraList = res.data.map(c => ({
                            label: c.camera_name, value: c.id,
                        }));
                        setCameras(cameraList);
                    }
                })
                .catch(err => warningToast(err?.detail || "Error fetching cameras"));
        }
    };


    return (<>            <Card className="example example-compact fixed-height-card">
            <CardBody className="custom-card-body">
                <Row className="align-items-center g-3">
                    <Col xs={12} lg={3} md={4} sm={12}>
                        <CardHeader title="Labels Details" className="p-2 "/>
                    </Col>
                    <Col xs={12} lg={9} md={8} sm={12}>
                        <div className="d-flex justify-content-md-end flex-wrap gap-2 mt-2 mt-md-0">
                            <div className="mr-3">
                                <SearchText reference={searchInput} onChangeHandler={filterResultManager}/>
                            </div>
                            <div className="d-flex flex-column flex-sm-row gap-2" style={{minWidth: 'fit-content'}}>
                                <CustomizedButtons
                                    className="btn btn-primary w-100 w-sm-auto mr-2"
                                    title="Add Label"
                                    submit={handleOpenAddLabelModal}
                                    color="primary"
                                    style={{minWidth: '120px'}}
                                />
                                <div style={{minWidth: '200px', maxWidth: '100%'}}>
                                    <ReactSelectDropDownCommon
                                        isSearchable
                                        placeholder="Select Company"
                                        value={selectedCompany}
                                        onChange={handleChangeCompany}
                                        options={companyOptions}
                                    />
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>

                <hr/>

                <div className={"table-wrapper-common"}>
                    {blocking ? (<div className="table-loader-wrapper">
                        <TableLoader rows={currentItems?.length} columns={columns.length}/>
                    </div>) : (<>
                        <PaginationProvider
                            pagination={paginationFactory(getPaginationOptions(filterEntities.length, resultManagerLabelUIProps.queryParams))}>
                            {({paginationProps, paginationTableProps}) => (<>
                                <AutoServingTable
                                    columns={columns}
                                    items={currentItems}
                                    tableChangeHandler={resultManagerLabelUIProps.setQueryParams}
                                    paginationTableProps={paginationTableProps}
                                    className={currentItems.length > 0 ? "table-wrapper-common" : "table-wrapper-common-no-data"}
                                    noDataIndication={() => (
                                        <div className="table-noDataIndication">No Data Found</div>)}
                                />
                                {currentItems.length > 0 && (<Pagination paginationProps={paginationProps}/>)}
                            </>)}
                        </PaginationProvider>

                    </>)}
                </div>
                <Modal show={addLabelModalOpen} onHide={() => setAddLabelModalOpen(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>{selectedUserId?.id ? 'Edit ' : 'Add'}Label</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mb-3">
                            <label>Company</label>
                            <ReactSelectDropDownCommon
                                options={companies}
                                value={companies.find(c => c.value === selectedCompanyId) || null}
                                onChange={handleCompanyChange}
                                placeholder="Select Company"
                            />
                        </div>
                        <div className="mb-3">
                            <label>Location</label>
                            <ReactSelectDropDownCommon
                                options={locations}
                                value={locations.find(l => l.value === selectedLocationId) || null}
                                onChange={handleLocationChange}
                                placeholder="Select Location"
                                isDisabled={!selectedCompanyId}
                            />
                        </div>
                        <div className="mb-3">
                            <label>Camera</label>
                            <ReactSelectDropDownCommon
                                options={cameras}
                                value={cameras.find(cam => cam.value === selectedCameraId) || null}
                                onChange={(cam) => setSelectedCameraId(cam?.value)}
                                placeholder="Select Camera"
                                isDisabled={!selectedLocationId}
                            />
                        </div>
                        <div className="mb-3">
                            <label>Label</label>
                            <input
                                type="text"
                                className="form-control"
                                value={labelText}
                                onChange={(e) => setLabelText(e.target.value)}
                                placeholder="Enter label"
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button className="btn btn-secondary" onClick={() => setAddLabelModalOpen(false)}>Cancel
                        </button>
                        <button className="btn btn-primary" onClick={handleSubmitLabel}>Save</button>
                    </Modal.Footer>
                </Modal>

            </CardBody>
        </Card>
            <Modal show={labelModalOpen} onHide={handleCloseLabelModal}>
                <Modal.Header closeButton>
                    <Modal.Title>All Labels</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedLabels.map((lbl, idx) => (<span key={idx} className="badge badge-secondary mr-2 mb-2">
        {lbl}
      </span>))}
                </Modal.Body>
            </Modal>
        </>

    );
}
